import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  ArrowLeft,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  BookOpen,
  Tag,
  Upload,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Calculator,
  ThumbsUp,
  ThumbsDown,
  Shield,
  Code2,
  Sparkles,
  Flame,
  Layers,
} from 'lucide-react';
import { Badge } from '../../components/Badge';
import { DashboardSkeleton } from '../../components/LoadingSkeleton';
import { pdfAnalysisService } from '../../services/pdf-analysis.service';
import { freeSourceService } from '../../services/free-source.service';
import type { FreeSourceTopicStatus } from '../../services/free-source.service';
import type {
  PdfAnalysis,
  PdfAnalysisItem,
  PdfAnalysisStatusResponse,
} from '../../types/pdf-analysis.types';

export function PdfAnalysisDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Poll status while processing
  const { data: statusData } = useQuery<PdfAnalysisStatusResponse>({
    queryKey: ['pdf-analysis-status', id],
    queryFn: () => pdfAnalysisService.getAnalysisStatus(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 3000;
      if (data.status === 'pending' || data.status === 'processing') return 3000;
      return false;
    },
  });

  // Fetch full analysis once completed (or on load if already done)
  const { data: analysis, isLoading } = useQuery<PdfAnalysis>({
    queryKey: ['pdf-analysis', id],
    queryFn: () => pdfAnalysisService.getAnalysis(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 5000;
      if (data.status === 'pending' || data.status === 'processing') return 5000;
      return false;
    },
  });

  const status = statusData?.status || analysis?.status || 'pending';
  const progress = statusData?.job?.progress || 0;
  const isProcessing = status === 'pending' || status === 'processing';
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';

  // NQG-RQ-26 v2: completed analysis without any extracted topics
  const hasNoTopics =
    isCompleted && !!analysis && ((analysis.totalItems ?? 0) === 0 || (analysis.items || []).length === 0);

  // Fetch the topic-identification status (pt-BR reason) only when relevant
  const { data: topicStatus } = useQuery<FreeSourceTopicStatus>({
    queryKey: ['free-source-topic-status', id],
    queryFn: () => freeSourceService.getTopicStatus(id!),
    enabled: !!id && hasNoTopics,
    retry: false,
  });

  if (isLoading) return <DashboardSkeleton />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 -m-6 p-6">
      <div className="max-w-[1800px] mx-auto space-y-6 animate-in">
        {/* Enhanced Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => navigate('/pdf-analysis')}
                className="p-2 hover:bg-white/80 rounded-xl transition-all duration-200 group"
              >
                <ArrowLeft className="w-5 h-5 text-gray-400 group-hover:text-[#1e73be] transition-colors" />
              </button>
              <div className="h-8 w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent" />
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-[#1e73be] via-[#4A90C8] to-[#ea7500] bg-clip-text text-transparent">
                  {analysis?.filename || 'PDF Analysis'}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {isCompleted
                    ? `${analysis?.totalItems || 0} topics extracted in ${analysis?.extractionMode || 'auto'} mode`
                    : isProcessing
                    ? 'Analysis in progress...'
                    : 'Analysis failed'}
                </p>
              </div>
            </div>
          </div>
        </div>

      {/* Processing State */}
      {isProcessing && (
        <ProcessingCard status={status} progress={progress} filename={analysis?.filename} />
      )}

      {/* Failed State */}
      {isFailed && (
        <div className="bg-gradient-to-r from-red-50 to-rose-50 rounded-2xl border-2 border-red-100 p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-red-100 rounded-xl">
              <XCircle className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <h3 className="font-bold text-red-800 text-lg">Analysis Failed</h3>
              <p className="text-sm text-red-600 mt-1">
                {analysis?.error || statusData?.error || 'An error occurred during processing.'}
              </p>
              <p className="text-xs text-red-400 mt-2">
                The job will automatically retry up to 3 times with exponential backoff.
              </p>
            </div>
          </div>
        </div>
      )}

        {/* Topics not identified (NQG-RQ-26 v2) */}
        {hasNoTopics && <TopicsNotIdentifiedCard analysisId={id!} reason={topicStatus?.reason} />}

        {/* Completed: Stats + Topics */}
        {isCompleted && analysis && (
          <>
            {/* Enhanced Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
              <MiniCard
                icon={FileText}
                label="Total Pages"
                value={analysis.totalPages}
                color="blue"
              />
              <MiniCard
                icon={BookOpen}
                label="Topics Extracted"
                value={analysis.totalItems}
                color="indigo"
              />
              <MiniCard
                icon={CheckCircle2}
                label="Easy"
                value={analysis.difficultyDistribution?.easy || 0}
                color="green"
              />
              <MiniCard
                icon={AlertTriangle}
                label="Medium"
                value={analysis.difficultyDistribution?.medium || 0}
                color="amber"
              />
              <MiniCard
                icon={Flame}
                label="Hard"
                value={analysis.difficultyDistribution?.hard || 0}
                color="red"
              />
            </div>

            {/* Analysis Topics — grouped by OE Code (hidden when no topics were identified) */}
            {!hasNoTopics && <GroupedTopics items={analysis.items || []} />}

            {/* Enhanced Formula Summary */}
            {analysis.formulaSummary && analysis.formulaSummary.length > 0 && (
              <div className="bg-white/60 backdrop-blur-sm rounded-3xl border-2 border-white/40 shadow-xl p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 bg-gradient-to-br from-purple-500/10 to-purple-600/20 rounded-xl">
                    <Calculator className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                      Formula Summary ({analysis.formulaSummary.length})
                    </h2>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {analysis.formulaSummary.map((f, i) => (
                    <div
                      key={i}
                      className="bg-gradient-to-br from-gray-50 to-purple-50/30 rounded-2xl border-2 border-purple-100 p-5 hover:shadow-lg transition-all"
                    >
                      <p className="font-bold text-gray-900 text-base">{f.name}</p>
                      <p className="text-sm text-gray-700 mt-2 font-mono bg-white rounded-xl px-3 py-2 border-2 border-gray-200 shadow-sm">
                        {f.formula}
                      </p>
                      {f.programmable && (
                        <p className="text-xs text-gray-500 mt-2 font-mono flex items-center gap-1">
                          <Code2 className="w-3.5 h-3.5" />
                          {f.programmable}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Meta info */}
            <div className="flex items-center justify-between text-xs text-gray-400 px-1">
              <div className="flex items-center gap-1.5">
                <Clock className="w-3 h-3" />
                Analyzed {new Date(analysis.analyzedAt).toLocaleString()}
              </div>
              <span>Mode: {analysis.extractionMode}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Topics Not Identified Card (NQG-RQ-26 v2) ───────────────────────────────

function TopicsNotIdentifiedCard({ analysisId, reason }: { analysisId: string; reason?: string }) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rescueError, setRescueError] = useState<string | null>(null);

  const rescueMutation = useMutation({
    mutationFn: (file: File) => freeSourceService.rescueAnalysis(analysisId, file),
    onSuccess: (result) => {
      setRescueError(null);
      // Rescued topics were written into this same analysis — refetch it
      queryClient.invalidateQueries({ queryKey: ['pdf-analysis', analysisId] });
      queryClient.invalidateQueries({ queryKey: ['pdf-analysis-status', analysisId] });
      queryClient.invalidateQueries({ queryKey: ['free-source-topic-status', analysisId] });
      console.log(
        `[Rescue] ${result.itemsAfter} topic(s) extracted` +
          (result.usedFallbackSingleTopic ? ' (whole-document fallback)' : '')
      );
    },
    onError: (error: any) => {
      setRescueError(error?.response?.data?.error || error?.message || 'Rescue extraction failed');
    },
  });

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) rescueMutation.mutate(file);
    e.target.value = ''; // allow re-selecting the same file
  };

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 p-6">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-amber-100 rounded-xl flex-shrink-0">
          <AlertTriangle className="w-6 h-6 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-amber-800 text-lg">
            No topics were identified in this document
          </h3>
          {reason && <p className="text-sm text-amber-700 mt-1">{reason}</p>}
          <p className="text-xs text-amber-500 mt-3">
            The analysis completed without extracted topics, so no questions can be generated from
            it yet. You can run a relaxed extraction — it finds topics using any structural or
            thematic cue and always yields at least one topic. Re-upload the same PDF to start.
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={handleFileSelected}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={rescueMutation.isPending}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
          >
            {rescueMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Extracting topics... this can take a few minutes
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Rescue — re-upload the PDF
              </>
            )}
          </button>

          {rescueError && (
            <p className="text-sm text-red-600 mt-3 font-medium">{rescueError}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Grouped Topics by OE Code ────────────────────────────────────────────────

function GroupedTopics({ items }: { items: PdfAnalysisItem[] }) {
  // Group items by oeCode
  const groups = items.reduce<Record<string, PdfAnalysisItem[]>>((acc, item) => {
    const key = item.oeCode || 'ungrouped';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const groupEntries = Object.entries(groups);
  const totalTopics = items.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gradient-to-br from-[#ea7500]/10 to-[#ea7500]/20 rounded-xl">
          <Sparkles className="w-6 h-6 text-[#ea7500]" />
        </div>
        <div>
          <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Analysis Topics ({totalTopics})
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {groupEntries.length} OE {groupEntries.length === 1 ? 'group' : 'groups'}
          </p>
        </div>
      </div>

      {groupEntries.map(([oeCode, groupItems]) => {
        const firstItem = groupItems[0];
        return (
          <div
            key={oeCode}
            className="bg-white/80 backdrop-blur-sm rounded-3xl border-2 border-gray-200 shadow-2xl overflow-hidden hover:shadow-3xl transition-all duration-300 hover:-translate-y-1"
          >
            {/* Enhanced Group header */}
            <div className="bg-gradient-to-r from-[#1e73be]/10 via-blue-50/60 to-[#ea7500]/10 px-6 py-5 border-b-2 border-gray-100">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-[#1e73be] to-[#4A90C8] rounded-2xl shadow-lg">
                  <Layers className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-base font-bold text-[#1e73be] bg-blue-100 px-4 py-1.5 rounded-xl border-2 border-blue-200 shadow-sm">
                      {oeCode}
                    </span>
                    <span className="px-3 py-1 bg-white rounded-xl text-xs font-bold text-gray-600 border-2 border-gray-200 shadow-sm">
                      {groupItems.length} {groupItems.length === 1 ? 'topic' : 'topics'}
                    </span>
                  </div>
                  {firstItem.oeText && (
                    <p className="text-sm text-gray-700 mt-2 font-medium">{firstItem.oeText}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Topics in this group */}
            <div className="p-6 space-y-4">
              {groupItems.map((item, index) => (
                <AnalysisItemCard key={index} item={item} index={index} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Processing Card ─────────────────────────────────────────────────────────

function ProcessingCard({
  status,
  progress,
  filename,
}: {
  status: string;
  progress: number;
  filename?: string;
}) {
  return (
    <div className="bg-gradient-to-br from-blue-50 via-indigo-50/30 to-purple-50/20 rounded-2xl border-2 border-blue-100 p-8 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-pulse pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center text-center">
        <div className="p-4 bg-white/80 rounded-2xl shadow-lg mb-4">
          <Loader2 className="w-10 h-10 text-[#1e73be] animate-spin" />
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-1">
          {status === 'pending' ? 'Queued for Processing' : 'AI Analysis in Progress'}
        </h3>
        <p className="text-sm text-gray-500 mb-5 max-w-md">
          {status === 'pending'
            ? `"${filename || 'PDF'}" is waiting in the queue. Processing will begin shortly.`
            : 'Claude is analyzing your PDF content and extracting structured data...'}
        </p>

        {/* Progress bar */}
        <div className="w-full max-w-sm">
          <div className="flex justify-between text-xs font-semibold text-gray-500 mb-1.5">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <div className="h-3 bg-white/80 rounded-full overflow-hidden shadow-inner">
            <div
              className="h-full bg-gradient-to-r from-[#1e73be] to-[#4A90C8] rounded-full transition-all duration-1000 ease-out relative"
              style={{ width: `${Math.max(progress, 3)}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
            </div>
          </div>
          <p className="text-[10px] text-gray-400 mt-2">
            This page auto-refreshes every 3 seconds
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Mini Stats Card ─────────────────────────────────────────────────────────

function MiniCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  color: string;
}) {
  const colorMap: Record<string, { bg: string; icon: string; gradient: string }> = {
    blue: {
      bg: 'bg-gradient-to-br from-blue-50 to-blue-100',
      icon: 'text-blue-600',
      gradient: 'from-blue-500 to-blue-600'
    },
    indigo: {
      bg: 'bg-gradient-to-br from-indigo-50 to-indigo-100',
      icon: 'text-indigo-600',
      gradient: 'from-indigo-500 to-indigo-600'
    },
    green: {
      bg: 'bg-gradient-to-br from-emerald-50 to-emerald-100',
      icon: 'text-emerald-600',
      gradient: 'from-emerald-500 to-emerald-600'
    },
    amber: {
      bg: 'bg-gradient-to-br from-amber-50 to-amber-100',
      icon: 'text-amber-600',
      gradient: 'from-amber-500 to-amber-600'
    },
    red: {
      bg: 'bg-gradient-to-br from-red-50 to-red-100',
      icon: 'text-red-600',
      gradient: 'from-red-500 to-red-600'
    },
  };

  const colors = colorMap[color] || colorMap.blue;

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl border-2 border-gray-200 p-5 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:border-gray-300">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-2xl ${colors.bg} shadow-lg`}>
          <Icon className={`w-6 h-6 ${colors.icon}`} />
        </div>
        <div>
          <p className={`text-3xl font-black bg-gradient-to-r ${colors.gradient} bg-clip-text text-transparent`}>
            {value}
          </p>
          <p className="text-xs text-gray-600 font-semibold mt-0.5">{label}</p>
        </div>
      </div>
    </div>
  );
}

// ── Analysis Item Card ──────────────────────────────────────────────────────

function AnalysisItemCard({
  item,
  index,
}: {
  item: PdfAnalysisItem;
  index: number;
}) {
  const [expanded, setExpanded] = useState(false);

  const difficultyColor: Record<string, string> = {
    easy: 'success',
    medium: 'warning',
    hard: 'error',
  };
  const confidenceColor: Record<string, string> = {
    high: 'success',
    medium: 'warning',
    low: 'error',
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-gray-200 overflow-hidden shadow-md hover:shadow-2xl hover:border-[#1e73be]/50 transition-all duration-300 hover:-translate-y-0.5">
      {/* Enhanced Header (always visible) */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-5 flex items-start gap-4 text-left hover:bg-gradient-to-r hover:from-blue-50/30 hover:to-purple-50/30 transition-all"
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-[#1e73be] to-[#4A90C8] flex items-center justify-center text-sm font-bold text-white shadow-md">
          {index + 1}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap mb-2">
            <span className="font-mono text-sm font-bold text-[#1e73be] bg-blue-100 px-3 py-1 rounded-xl border-2 border-blue-200 shadow-sm">
              {item.oeCode}
            </span>
            <Badge variant={difficultyColor[item.difficulty] as any} size="sm">
              {item.difficulty}
            </Badge>
            <Badge variant={confidenceColor[item.confidence] as any} size="sm">
              {item.confidence} conf.
            </Badge>
          </div>
          <h4 className="font-bold text-gray-900 text-base leading-tight">{item.topics}</h4>
          {item.oeText && (
            <p className="text-sm text-gray-600 mt-1.5 line-clamp-2">{item.oeText}</p>
          )}
        </div>

        <div className={`flex-shrink-0 p-2 rounded-xl transition-all ${
          expanded ? 'bg-gray-200' : 'bg-gray-100 hover:bg-gray-200'
        }`}>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-600" />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-4 animate-in">
          {/* Description */}
          {item.description && (
            <div>
              <SectionLabel icon={Lightbulb} label="Description" />
              <p className="text-sm text-gray-700 leading-relaxed">{item.description}</p>
            </div>
          )}

          {/* Keywords */}
          {item.keywords?.length > 0 && (
            <div>
              <SectionLabel icon={Tag} label="Keywords" />
              <div className="flex flex-wrap gap-1.5">
                {item.keywords.map((kw, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Concepts */}
          {item.concepts?.length > 0 && (
            <div>
              <SectionLabel icon={BookOpen} label="Concepts" />
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
                {item.concepts.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Formulas */}
          {item.formulas?.length > 0 && (
            <div>
              <SectionLabel icon={Calculator} label={`Formulas (${item.formulas.length})`} />
              <div className="space-y-2">
                {item.formulas.map((f, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg border border-gray-100 p-3"
                  >
                    <p className="text-sm font-semibold text-gray-900">{f.name}</p>
                    <p className="text-xs font-mono text-gray-600 mt-1 bg-gray-50 px-2 py-1 rounded">
                      {f.expression}
                    </p>
                    {f.notes && (
                      <p className="text-xs text-gray-400 mt-1">{f.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Advantages & Disadvantages */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {item.vantagens?.length > 0 && (
              <div>
                <SectionLabel icon={ThumbsUp} label="Advantages" />
                <ul className="space-y-1">
                  {item.vantagens.map((v, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-sm text-gray-700">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                      {v}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {item.desvantagens?.length > 0 && (
              <div>
                <SectionLabel icon={ThumbsDown} label="Disadvantages" />
                <ul className="space-y-1">
                  {item.desvantagens.map((d, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-sm text-gray-700">
                      <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Exam Alerts */}
          {item.examAlerts?.length > 0 && (
            <div>
              <SectionLabel icon={Shield} label="Exam Alerts" />
              <div className="space-y-1.5">
                {item.examAlerts.map((alert, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 text-sm bg-amber-50 text-amber-800 rounded-lg px-3 py-2 border border-amber-100"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    {alert}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Solved Examples */}
          {item.solvedExamples?.length > 0 && (
            <div>
              <SectionLabel icon={Code2} label={`Solved Examples (${item.solvedExamples.length})`} />
              <div className="space-y-3">
                {item.solvedExamples.map((ex, i) => (
                  <div
                    key={i}
                    className="bg-gradient-to-r from-indigo-50/50 to-purple-50/30 rounded-xl border border-indigo-100 p-4"
                  >
                    <p className="font-semibold text-gray-900 text-sm">{ex.title}</p>
                    <p className="text-xs text-gray-600 mt-1">{ex.problem}</p>

                    {ex.steps?.length > 0 && (
                      <div className="mt-3 space-y-1.5">
                        {ex.steps.map((step) => (
                          <div
                            key={step.step}
                            className="flex items-start gap-2 text-xs"
                          >
                            <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold flex-shrink-0 text-[10px]">
                              {step.step}
                            </span>
                            <div>
                              <span className="text-gray-700">{step.description}</span>
                              <span className="font-mono text-indigo-600 ml-1">
                                {step.calculation}
                              </span>
                              {step.result !== null && (
                                <span className="text-gray-500 ml-1">= {step.result}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-3 flex items-center gap-2 text-xs">
                      <span className="font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded">
                        Answer: {ex.answer}
                      </span>
                      {ex.unit && (
                        <span className="text-gray-400">({ex.unit})</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Section Label ───────────────────────────────────────────────────────────

function SectionLabel({
  icon: Icon,
  label,
}: {
  icon: React.ElementType;
  label: string;
}) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <Icon className="w-3.5 h-3.5 text-gray-400" />
      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
        {label}
      </span>
    </div>
  );
}

export default PdfAnalysisDetail;
